import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart3, Search, ChevronRight, Zap, Grid3X3, List, X, Trophy, ExternalLink, TrendingUp } from 'lucide-react';

function RtpBadge({ rtp }) {
  const color = rtp >= 98 ? '#10b981' : rtp >= 95 ? '#f59e0b' : '#ef4444';
  const bg = rtp >= 98 ? 'rgba(16,185,129,0.15)' : rtp >= 95 ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)';
  return (
    <span className="text-xs font-bold px-2 py-1 rounded-md" style={{ color, background: bg }} data-testid="rtp-badge">
      {rtp}%
    </span>
  );
}

export default function RTPComparisonEngine() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [gameType, setGameType] = useState('all');
  const [provider, setProvider] = useState('all');
  const [minRtp, setMinRtp] = useState([90]);
  const [volatility, setVolatility] = useState('all');
  const [sortBy, setSortBy] = useState('rtp_desc');
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchGames = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sort_by: sortBy, min_rtp: minRtp[0] };
      if (search) params.search = search;
      if (gameType !== 'all') params.game_type = gameType;
      if (provider !== 'all') params.provider = provider;
      if (volatility !== 'all') params.volatility = volatility;
      const res = await api.get('/tools/games', { params });
      setGames(res.data.games);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
      if (res.data.providers) setProviders(res.data.providers);
    } catch (err) {
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  }, [search, gameType, provider, minRtp, volatility, sortBy, page]);

  useEffect(() => { fetchGames(); }, [fetchGames]);
  useEffect(() => { setPage(1); }, [search, gameType, provider, minRtp, volatility, sortBy]);

  const toggleSelect = (game) => {
    setSelected(prev => {
      const exists = prev.find(g => g.game_id === game.game_id);
      if (exists) return prev.filter(g => g.game_id !== game.game_id);
      if (prev.length >= 4) { toast.error('Maximum 4 games for comparison'); return prev; }
      return [...prev, game];
    });
  };

  const isSelected = (gameId) => selected.some(g => g.game_id === gameId);

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="rtp-comparison-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe] transition-colors">Dashboard</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Play Smart</span>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-white">RTP Comparison Engine</span>
      </nav>

      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#10b981]/15 flex items-center justify-center shrink-0">
          <BarChart3 className="h-6 w-6 text-[#10b981]" />
        </div>
        <div>
          <h1 className="text-2xl lg:text-[32px] font-bold text-white" style={{ letterSpacing: '-0.005em' }}>RTP Comparison Engine</h1>
          <p className="text-[#a0a8b8] text-sm mt-1">Compare Return-to-Player rates across 5,000+ slot games and table games</p>
          <div className="flex items-center gap-1.5 mt-2 text-xs text-[#64748b]"><TrendingUp className="h-3 w-3" /> 15,300 uses this month</div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full h-12 pl-11 pr-4 rounded-xl bg-[#20263a] border border-white/10 text-white text-sm placeholder:text-[#64748b] ciq-input focus:outline-none"
          placeholder="Search games (e.g., 'Book of Dead', 'Mega Moolah')" data-testid="rtp-search-input" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={gameType} onValueChange={setGameType}>
          <SelectTrigger className="w-[150px] h-10 bg-[#20263a] border-white/10 text-white text-sm" data-testid="rtp-filter-type">
            <SelectValue placeholder="Game Type" />
          </SelectTrigger>
          <SelectContent className="bg-[#252b3b] border-white/10">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Slots">Slots</SelectItem>
            <SelectItem value="Table Games">Table Games</SelectItem>
            <SelectItem value="Video Poker">Video Poker</SelectItem>
          </SelectContent>
        </Select>

        <Select value={provider} onValueChange={setProvider}>
          <SelectTrigger className="w-[170px] h-10 bg-[#20263a] border-white/10 text-white text-sm" data-testid="rtp-filter-provider">
            <SelectValue placeholder="Provider" />
          </SelectTrigger>
          <SelectContent className="bg-[#252b3b] border-white/10">
            <SelectItem value="all">All Providers</SelectItem>
            {providers.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={volatility} onValueChange={setVolatility}>
          <SelectTrigger className="w-[140px] h-10 bg-[#20263a] border-white/10 text-white text-sm" data-testid="rtp-filter-volatility">
            <SelectValue placeholder="Volatility" />
          </SelectTrigger>
          <SelectContent className="bg-[#252b3b] border-white/10">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="Low">Low</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="High">High</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-[#20263a] border border-white/10 min-w-[200px]">
          <span className="text-xs text-[#64748b] whitespace-nowrap">Min RTP: {minRtp[0]}%</span>
          <Slider value={minRtp} onValueChange={setMinRtp} min={90} max={99} step={0.5} className="flex-1" data-testid="rtp-filter-slider" />
        </div>
      </div>

      {/* Sort & View */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px] h-9 bg-[#20263a] border-white/10 text-white text-xs" data-testid="rtp-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#252b3b] border-white/10">
              <SelectItem value="rtp_desc">Highest RTP</SelectItem>
              <SelectItem value="rtp_asc">Lowest RTP</SelectItem>
              <SelectItem value="name_asc">Name A-Z</SelectItem>
              <SelectItem value="name_desc">Name Z-A</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-[#64748b]">{total} games found</span>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-[#4d7cfe]/15 text-[#4d7cfe]' : 'text-[#64748b] hover:text-white'}`} data-testid="rtp-view-grid"><Grid3X3 className="h-4 w-4" /></button>
          <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-[#4d7cfe]/15 text-[#4d7cfe]' : 'text-[#64748b] hover:text-white'}`} data-testid="rtp-view-list"><List className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Games Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="ciq-card"><Skeleton className="h-24 w-full bg-[#2d3548] rounded-lg mb-3" /><Skeleton className="h-4 w-3/4 bg-[#2d3548]" /><Skeleton className="h-3 w-1/2 bg-[#2d3548] mt-2" /></div>
          ))}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {games.map((game) => (
            <div key={game.game_id} className={`ciq-card ciq-card-clickable group ${isSelected(game.game_id) ? 'border-[#4d7cfe] ring-1 ring-[#4d7cfe]/30' : ''}`} data-testid={`game-card-${game.game_id}`}>
              <div className="h-20 rounded-lg bg-gradient-to-br from-[#252b3b] to-[#1a1f2e] flex items-center justify-center mb-3 relative">
                <span className="text-2xl font-bold text-white/10">{game.name.charAt(0)}</span>
                <div className="absolute top-2 right-2"><RtpBadge rtp={game.rtp_percentage} /></div>
              </div>
              <h4 className="text-white font-medium text-sm truncate">{game.name}</h4>
              <p className="text-[#64748b] text-xs mt-0.5">{game.provider}</p>
              <div className="flex items-center gap-4 mt-3 text-xs text-[#a0a8b8]">
                <span className="flex items-center gap-1"><Zap className="h-3 w-3" /> {game.volatility}</span>
                {game.paylines > 0 && <span className="flex items-center gap-1"><Grid3X3 className="h-3 w-3" /> {game.paylines.toLocaleString()}</span>}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => toggleSelect(game)}
                  className={`flex-1 h-8 rounded-lg text-xs font-medium transition-colors ${isSelected(game.game_id) ? 'bg-[#4d7cfe] text-white' : 'border border-white/10 text-[#a0a8b8] hover:border-[#4d7cfe]/30 hover:text-white'}`}
                  data-testid={`compare-btn-${game.game_id}`}>
                  {isSelected(game.game_id) ? 'Selected' : 'Compare'}
                </button>
                <Link to="/casino-partners" className="flex-1 h-8 rounded-lg border border-white/10 text-xs text-[#a0a8b8] hover:border-[#4d7cfe]/30 hover:text-white flex items-center justify-center gap-1 transition-colors" data-testid={`find-casino-btn-${game.game_id}`}>
                  Find Casino <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {games.map((game) => (
            <div key={game.game_id} className={`ciq-card flex items-center gap-4 py-3 ${isSelected(game.game_id) ? 'border-[#4d7cfe] ring-1 ring-[#4d7cfe]/30' : ''}`} data-testid={`game-row-${game.game_id}`}>
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#252b3b] to-[#1a1f2e] flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-white/20">{game.name.charAt(0)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-medium text-sm truncate">{game.name}</h4>
                <p className="text-[#64748b] text-xs">{game.provider}</p>
              </div>
              <RtpBadge rtp={game.rtp_percentage} />
              <span className="text-xs text-[#a0a8b8] hidden sm:block w-16">{game.volatility}</span>
              <button onClick={() => toggleSelect(game)}
                className={`h-8 px-3 rounded-lg text-xs font-medium transition-colors ${isSelected(game.game_id) ? 'bg-[#4d7cfe] text-white' : 'border border-white/10 text-[#a0a8b8] hover:text-white'}`}>
                {isSelected(game.game_id) ? 'Selected' : 'Compare'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="px-4 h-9 rounded-lg border border-white/10 text-sm text-[#a0a8b8] hover:text-white disabled:opacity-30 transition-colors" data-testid="rtp-prev-page">Previous</button>
          <span className="text-sm text-[#64748b]">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="px-4 h-9 rounded-lg border border-white/10 text-sm text-[#a0a8b8] hover:text-white disabled:opacity-30 transition-colors" data-testid="rtp-next-page">Next</button>
        </div>
      )}

      {/* Comparison Panel */}
      {selected.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#252b3b]/95 backdrop-blur-lg border-t border-white/10 z-40 animate-slide-up" data-testid="comparison-panel">
          <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="text-sm text-[#a0a8b8] shrink-0">Comparing ({selected.length}/4)</span>
              <div className="flex gap-2 overflow-x-auto">
                {selected.map(g => (
                  <span key={g.game_id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#4d7cfe]/15 text-[#4d7cfe] text-xs whitespace-nowrap">
                    {g.name} ({g.rtp_percentage}%)
                    <button onClick={() => toggleSelect(g)} className="hover:text-white"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>
            <button onClick={() => setShowCompare(true)}
              className="ml-3 px-5 h-10 rounded-xl bg-[#4d7cfe] text-white text-sm font-medium btn-lift shrink-0"
              data-testid="view-comparison-btn">View Full Comparison</button>
          </div>
        </div>
      )}

      {/* Comparison Modal */}
      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent className="bg-[#252b3b] border-white/10 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">Game Comparison</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 text-[#a0a8b8] font-medium">Game</th>
                  <th className="text-center py-2 text-[#a0a8b8] font-medium">RTP</th>
                  <th className="text-center py-2 text-[#a0a8b8] font-medium">House Edge</th>
                  <th className="text-center py-2 text-[#a0a8b8] font-medium">Volatility</th>
                  <th className="text-center py-2 text-[#a0a8b8] font-medium">Max Win</th>
                  <th className="text-center py-2 text-[#a0a8b8] font-medium">Casinos</th>
                </tr>
              </thead>
              <tbody>
                {selected.map((g, i) => (
                  <tr key={g.game_id} className={`border-b border-white/5 ${i === 0 ? 'bg-[#4d7cfe]/5' : ''}`} data-testid={`compare-row-${g.game_id}`}>
                    <td className="py-3"><div className="text-white font-medium">{g.name}</div><div className="text-[#64748b] text-xs">{g.provider}</div></td>
                    <td className="text-center"><RtpBadge rtp={g.rtp_percentage} /></td>
                    <td className="text-center text-[#f59e0b]">{(100 - g.rtp_percentage).toFixed(2)}%</td>
                    <td className="text-center text-[#a0a8b8]">{g.volatility}</td>
                    <td className="text-center text-white">{g.max_win}</td>
                    <td className="text-center"><Link to="/casino-partners" className="text-[#4d7cfe] text-xs hover:underline">See Casinos</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {selected.length > 0 && (
            <div className="mt-4 p-4 rounded-xl bg-[#10b981]/10 border border-[#10b981]/20" data-testid="comparison-winner">
              <div className="flex items-center gap-2 mb-1">
                <Trophy className="h-4 w-4 text-[#10b981]" />
                <span className="text-sm font-semibold text-[#10b981]">Winner: {selected.sort((a, b) => b.rtp_percentage - a.rtp_percentage)[0]?.name}</span>
              </div>
              <p className="text-xs text-[#a0a8b8]">
                Highest RTP at {selected.sort((a, b) => b.rtp_percentage - a.rtp_percentage)[0]?.rtp_percentage}%, giving you the best expected return over time.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
